import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Clock, FileCheck, MapPin } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { EPC_TOWNS, EPC_PRICING } from "@/lib/epc";
import { BRAND } from "@/lib/content";

const STEPS = [
  { icon: Clock, title: "Same-week visit", text: "A single, discreet appointment — typically 45–60 minutes of photographs, measurements and heating details." },
  { icon: FileCheck, title: "Lodged promptly", text: "Your certificate is lodged on the national EPC register shortly after the visit, ready for sales, lettings and legal use." },
  { icon: BadgeCheck, title: "Valid 10 years", text: "One certificate covers successive sales and tenancies for a decade — with honest advice on lifting the band included." },
];

export default function EpcHub() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Domestic EPCs in Lincolnshire",
    serviceType: "Energy Performance Certificate",
    provider: {
      "@type": "ProfessionalService",
      name: "Declan Bishop — Property Consultant",
      url: "https://dbpropertyconsultant.co.uk",
      telephone: "+44 7947 981 727",
    },
    areaServed: EPC_TOWNS.map((t) => ({ "@type": "City", name: t.name })),
    offers: {
      "@type": "Offer",
      price: "85",
      priceCurrency: "GBP",
      description: `EPCs from ${EPC_PRICING.from}. ${EPC_PRICING.note}`,
    },
    url: "https://dbpropertyconsultant.co.uk/epc",
  };

  return (
    <SiteLayout
      pageTitle="EPCs from £85 — Accredited Energy Assessor"
      pageDescription="Domestic EPCs from £85 across Lincolnshire — Coningsby, Tattershall, Woodhall Spa, Horncastle, Boston and beyond. Same-week appointments, fixed quotes, lodged on the national register."
      pagePath="/epc"
    >
      <JsonLd id="epc-hub-schema" data={schema} />

      <section data-testid="epc-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="epc-overline">Domestic EPCs · Lincolnshire</div>
          <h1
            data-testid="epc-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            EPCs from <span className="italic font-light text-brand-champagne">{EPC_PRICING.from}.</span>{" "}
            Same-week. Accredited.
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            Accredited domestic Energy Performance Certificates for sales and lettings,
            lodged on the national register and valid for 10 years — with plain-English
            advice on lifting the rating included.
          </p>
          <p data-testid="epc-pricing-note" className="mt-5 max-w-2xl text-sm text-brand-navy/60 leading-relaxed">
            {EPC_PRICING.note}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="epc-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Get a Fixed Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="epc-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section data-testid="epc-steps" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {STEPS.map((s) => (
              <div key={s.title} className="bg-white p-8 md:p-10">
                <s.icon size={22} className="text-brand-champagne" />
                <h2 className="mt-5 font-serif text-2xl text-brand-navy">{s.title}</h2>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-brand-navy/60 max-w-3xl leading-relaxed">
            Letting a property? The minimum standard is currently band E — read{" "}
            <Link to="/blog/epc-guide-for-landlords-lincolnshire" className="ink-link font-medium text-brand-navy" data-testid="epc-guide-link">
              the landlord&apos;s guide to EPCs
            </Link>{" "}
            for the rules, the proposed move to band C, and five ways to climb the bands.
          </p>
        </div>
      </section>

      <section data-testid="epc-towns" className="bg-brand-cream">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">EPCs near you</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight max-w-2xl">
            Local EPC guides for your town.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {EPC_TOWNS.map((t) => (
              <Link
                key={t.slug}
                to={`/epc/${t.slug}`}
                data-testid={`epc-town-card-${t.slug}`}
                className="group bg-brand-cream p-8 md:p-10 hover:bg-white transition-colors"
              >
                <MapPin size={18} className="text-brand-champagne" />
                <h3 className="mt-5 font-serif text-2xl md:text-3xl text-brand-navy tracking-tight">
                  EPC in {t.name}
                </h3>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed line-clamp-3">{t.intro}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link">
                  From {EPC_PRICING.from}
                  <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
            <div className="bg-brand-navy p-8 md:p-10 text-brand-cream">
              <MapPin size={18} className="text-brand-champagne" />
              <h3 className="mt-5 font-serif text-2xl md:text-3xl tracking-tight">Somewhere else?</h3>
              <p className="mt-3 text-sm text-brand-cream/75 leading-relaxed">
                EPCs are available throughout Lincolnshire — Lincoln, Grantham, Sleaford,
                Spalding, Louth, Skegness and beyond.
              </p>
              <Link
                to="/contact"
                data-testid="epc-town-card-other"
                className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-champagne hover:text-brand-cream transition-colors"
              >
                Request a quote
                <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
